"use client";
import useLocalStorage, { deleteFromStorage } from "@rehooks/local-storage";
import { AUTH_IDENTITY_TOKEN, STORAGE_KEY_DEVICE_PLATFORM } from "@/utils/constants/storageKeys";
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import CustomerDto from "@/projectRoot/api-client/interfaces/models/customers/Customer.dto";
import { useGlobals } from "./GlobalsContext";
import { uuid } from "../utils/handlers/miscHandler";
import { useTranslations } from "use-intl";
import { useApiClient } from "./ApiClientContext";
import { usePathname } from "next/navigation";


export interface ProfileContextProps {
    identity?: CustomerDto;
    logout: () => void;
    setIdentity?: Dispatch<SetStateAction<CustomerDto>>;
    updateWishlist?: (productId: string, action: 'add' | 'remove') => void;
}

export const defaultContext: ProfileContextProps = {
    identity: null,
    logout: () => { },
};

export const ProfileContext = createContext<ProfileContextProps>(defaultContext);

export type ContextProps = Partial<ProfileContextProps> & { children?: any };

export default function ProfileProvider({ children }: ContextProps) {
    const { apiClient } = useApiClient();
    const [identityTokenData] = useLocalStorage<{ identity_token: string, expires_at: string }>(AUTH_IDENTITY_TOKEN);
    const [identity, setIdentity] = useState(null);
    const { toasts, setToasts } = useGlobals();
    const t = useTranslations();
    const pathname = usePathname();
    const [devicePlatform] = useLocalStorage(STORAGE_KEY_DEVICE_PLATFORM);

    //FUNCTIONS

    const isTokenValid = (identity_token?: { expires_at: string }) => identity_token && new Date().getTime() < Date.parse(identity_token.expires_at);

    const logout = () => {
        deleteFromStorage(AUTH_IDENTITY_TOKEN);
        // sessionStorage.removeItem(STORAGE_KEY_CURRENT_CART);
        setIdentity(null);
        setTimeout(() => {
            setToasts([...toasts, {
                id: uuid(),
                description: `<p><img style="margin-right: 20px;" src='/img/icons/activated-checkmark.svg' alt='checkmark' />${t('successful_logout')}</p>`,
                initiation_time: new Date().getTime(),
                duration_time: 10000
            }]);
        }, 1000);
    };

    const updateWishlist = async (productId: string, action: 'add' | 'remove') => {
        try {
            const response = action === 'add'
                ? await apiClient.profile().wishlistAdd({ product_id: productId })
                : await apiClient.profile().wishlistRemove(productId);

            if (response.status == 200 || response.status === 201) {
                const message = action === 'add'
                    ? t('successful_wishlist_add')
                    : t('successful_wishlist_remove');

                setTimeout(() => {
                    setToasts([...toasts, {
                        id: uuid(),
                        description: `<p><img style="margin-right: 20px;" src='/img/icons/activated-checkmark.svg' alt='checkmark' />${message}</p>`,
                        initiation_time: new Date().getTime(),
                        duration_time: 1500
                    }]);
                }, 1000);
            }
        } catch (err) {
            console.log(err);
        }
    }

    const getIdentity = async () => {
        try {
            const response = await apiClient.profile().identity();
            if (response.data) {
                setIdentity(response.data);
            }
        } catch (err) {
            console.log(err);
        }
    }

    // EFFECTS

    useEffect(() => {
        if (!identityTokenData) {
            return;
        }

        if (!isTokenValid(identityTokenData)) {
            logout();
            return;
        }

        apiClient.profile().identity()
            .then(response => {
                if (response.data) {
                    setIdentity(response.data);
                } else {
                    logout();
                }
            }).catch((err) => {
                console.log(err)
                logout();
            });
    }, [identityTokenData]);

    useEffect(() => {
        if (devicePlatform && !pathname.includes('/ws/checkout')) {
            deleteFromStorage(STORAGE_KEY_DEVICE_PLATFORM);
            logout();
        }
    }, [pathname]);

    return (
        <ProfileContext.Provider value={{
            identity,
            logout,
            setIdentity,
            updateWishlist,
        }}>
            {children}
        </ProfileContext.Provider>
    )
}

export function useProfile(): ProfileContextProps {
    return useContext<ProfileContextProps>(ProfileContext);
}